/**
 * Overlay Math Utilities
 *
 * Pure functions for overlay positioning, scaling, and rotation calculations.
 * Single source of truth for percent ↔ pixel conversions and angle math.
 */

/**
 * Clamp a value between min and max (inclusive).
 */
export function clamp(val: number, min: number, max: number): number {
  return Math.min(Math.max(val, min), max);
}

/**
 * Normalize degrees to 0-360 range.
 */
export function normalizeDeg(deg: number): number {
  let normalized = deg % 360;
  if (normalized < 0) {
    normalized += 360;
  }
  return normalized;
}

/**
 * Convert pixels to percentage (0-100) based on container size.
 * Returns 0 if containerSize is 0 or negative (prevents division by zero).
 */
export function pxToPct(px: number, containerSize: number): number {
  if (containerSize <= 0) return 0;
  return (px / containerSize) * 100;
}

/**
 * Convert percentage (0-100) to pixels based on container size.
 */
export function pctToPx(pct: number, containerSize: number): number {
  return (pct / 100) * containerSize;
}

/**
 * Calculate angle (in degrees) from center point to target point.
 * Returns angle in degrees, 0-360 (0 = right, 90 = down, 180 = left, 270 = up).
 */
export function angleBetweenPoints(
  cx: number,
  cy: number,
  px: number,
  py: number
): number {
  const dx = px - cx;
  const dy = py - cy;
  const radians = Math.atan2(dy, dx);
  const degrees = (radians * 180) / Math.PI;
  return normalizeDeg(degrees);
}

/**
 * Compute the shortest signed delta (in degrees) between two angles.
 * Result is in [-180, 180].
 *
 * Useful for smooth rotation across 0/360 wrap.
 */
export function shortestDeltaDeg(currentDeg: number, startDeg: number): number {
  return ((currentDeg - startDeg + 540) % 360) - 180;
}

/**
 * Calculate Euclidean distance between two points.
 */
export function distance(
  p1: { x: number; y: number },
  p2: { x: number; y: number }
): number {
  const dx = p2.x - p1.x;
  const dy = p2.y - p1.y;
  return Math.sqrt(dx * dx + dy * dy);
}
